import React, {createContext, useContext, useMemo} from 'react';

import {useMemoizedSelector} from '../../state/hooks';
import {useRunSetProject} from '../../state/views/runSet/hooks';
import {type Ref as RunSetRef} from '../../state/views/runSet/types';
import type {
  Ref as SortRef,
  Sort as SortType,
} from '../../state/views/sort/types';
import type {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {
  makeRunSetGroupingSelector,
  makeRunSetGroupSelectionsRefSelector,
  makeRunSetPinnedRunIdsSelector,
  makeRunSetPinningSelector,
  makeRunSetProjectSelector,
  makeRunSetSortRefSelector,
  makeRunSetSortSelector,
  makeRunSetTruncationTypeSelector,
} from './runSetSelectors';

export type RunSetRefContextType = {
  ref: RunSetRef;
} | null;

/**
 * @deprecated Use in unit tests only please. For application code, use the
 *             higher-level hooks in this file instead.
 */
export const RunSetRefContext = createContext<RunSetRefContextType | null>(
  null
);

type RunSetRefContextProviderProps = {
  children: React.ReactNode;
  runSetRef: RunSetRef;
};

/**
 * Provides the runSetRef via context for the RunSelector component tree.
 *
 * ## Usage
 * Consumers should use the selector hooks in this file (e.g., usePinnedRunIds,
 * useGroupSelectionsRef) to subscribe to only the properties they need,
 * avoiding unnecessary re-renders.
 *
 * ## ⚠️ WARNING: Do NOT use usePart(runSetRef) in this context tree!
 *
 * Calling `usePart(runSetRef)` subscribes to the ENTIRE run set object and
 * triggers re-renders on ANY property change (filters, selections, sort, pins,
 * grouping, etc.). Since the RunSelector tree has many deeply nested components,
 * this causes expensive cascading re-renders throughout the entire tree.
 *
 * Instead, use the memoized selector hooks provided here, which only subscribe
 * to specific properties and use reference equality for efficient updates.
 */
export function RunSetRefContextProvider({
  children,
  runSetRef,
}: RunSetRefContextProviderProps) {
  const value = useMemo(
    () => ({
      ref: runSetRef,
    }),
    [runSetRef]
  );

  return (
    <RunSetRefContext.Provider value={value}>
      {children}
    </RunSetRefContext.Provider>
  );
}

export function useCurrentRunSetRef() {
  const val = useContext(RunSetRefContext);

  if (!val) {
    throw new Error('Unexpected empty run set info in provider');
  }

  // Ensure context only contains the ref - not the resolved run set object.
  // If you adds more properties to the context, please read the comment above {@link RunSetRefContextProvider}
  const keys = Object.keys(val);
  if (keys.length !== 1 || keys[0] !== 'ref') {
    throw new Error(
      'RunSetRefContext should only contain ref. ' +
        'Do NOT add resolved run set data to this context - use selector hooks instead.'
    );
  }

  return val.ref;
}

// =============================================================================
// Convenience Hooks (use context + reselect selectors)
// =============================================================================
//
// These hooks combine useCurrentRunSetRef() with useMemoizedSelector() and
// the selector factories in runSetSelectors.ts. They provide a simple API for consumers
// while using standard reselect patterns under the hood.
// =============================================================================

/**
 * Returns the project for the current run set.
 */
export function useRunSelectorProjectInfo(): {
  entityName: string;
  projectName: string;
  projectInternalId?: string;
} {
  const ref = useCurrentRunSetRef();
  const project = useMemoizedSelector(makeRunSetProjectSelector, ref);
  return useRunSetProject(project);
}

/**
 * Returns the group selections ref for the current run set.
 */
export const useRunSetGroupSelectionsRef = () => {
  const ref = useCurrentRunSetRef();
  return useMemoizedSelector(makeRunSetGroupSelectionsRefSelector, ref);
};

/**
 * Returns the grouping for the current run set.
 */
export const useRunSetGroupSelectionGrouping = () => {
  const ref = useCurrentRunSetRef();
  return useMemoizedSelector(makeRunSetGroupingSelector, ref);
};

/**
 * Returns pinned run IDs and baseline run ID together.
 * Use this when you need both values (avoids two separate subscriptions).
 */
export const usePinnedAndBaseline = () => {
  const ref = useCurrentRunSetRef();
  return useMemoizedSelector(makeRunSetPinningSelector, ref);
};

/**
 * Returns only the pinnedRunIds array for the current run set.
 * Use this when you only need pinnedRunIds and don't need baselineRunId.
 */
export const usePinnedRunIdsOnly = () => {
  const ref = useCurrentRunSetRef();
  return useMemoizedSelector(makeRunSetPinnedRunIdsSelector, ref);
};

/**
 * Returns the current sort for the run set.
 */
export const useRunSetSort = (): SortType => {
  const ref = useCurrentRunSetRef();
  return useMemoizedSelector(makeRunSetSortSelector, ref);
};

/**
 * Returns the sortRef for the current run set (for dispatching sort updates).
 */
export const useRunSetSortRef = (): SortRef => {
  const ref = useCurrentRunSetRef();
  return useMemoizedSelector(makeRunSetSortRefSelector, ref);
};

/**
 * Returns the run name truncation type for the current run set.
 */
export const useRunSetTruncationType = (): TruncationType => {
  const ref = useCurrentRunSetRef();
  return useMemoizedSelector(makeRunSetTruncationTypeSelector, ref);
};
